import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ApiError } from "../lib/api";
import { cn } from "../lib/utils";
import {
  type OppositionRequete,
  oppositionDiscordLeveeReponseSchema,
  oppositionDiscordReponseSchema,
  oppositionLeveeReponseSchema,
  oppositionReponseSchema,
} from "./contract";
import {
  OPPOSITION_DISCORD_LEVEE_MESSAGES,
  OPPOSITION_FAILED,
  OPPOSITION_LEVEE_FAILED,
  OPPOSITION_LEVEE_MESSAGES,
  OPPOSITION_MESSAGES,
} from "./regie-texts";
import { CARTE, TITRE_CARTE } from "./reglages/cadre";

/**
 * « Effacer un viewer a sa demande » : la voie par laquelle Nicolas tient la promesse de la politique
 * de confidentialite (« vos donnees sont alors effacees, et La Taverne ne note plus rien de vous »).
 * Elle appelle `POST /api/twitch/opposition` (`backend/src/app/twitch/opposition.ts`), derriere la
 * garde de la regie.
 *
 * Pour Twitch, deux champs, le login Twitch ou l'identifiant Twitch (un login peut n'etre que des chiffres, un seul
 * champ ne saurait pas lequel c'est) ; l'identifiant l'emporte s'ils sont remplis tous les deux. Un
 * effacement ne se defait pas : le bouton demande une confirmation, qui rappelle ce qui va etre efface.
 * Un choix « Twitch » ou « Discord » : pour Discord, un seul champ, l'identifiant (17 a 20 chiffres), vers
 * `POST /api/viewers/opposition-discord`. Le resultat est une phrase ecrite par la page (`regie-texts.ts`), jamais un texte du serveur.
 */

/** Une requete JSON en POST : le resultat est lu par le contrat, tout le reste est un echec. */
async function oppositionRequest(corps: OppositionRequete) {
  const response = await fetch("/api/twitch/opposition", {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(corps),
  });
  const payload: unknown = await response.json().catch(() => null);
  const result = oppositionReponseSchema.safeParse(payload);
  if (!response.ok || !result.success) {
    throw new ApiError("opposition_failed", "Effacement en echec", response.status);
  }
  return result.data;
}

/** Un identifiant Discord : 17 a 20 chiffres, comme le contrat (`shared/src/app/ecuyer.ts`). */
const IDENTIFIANT_DISCORD = /^\d{17,20}$/;

/** L'opposition d'un membre Discord (`POST /api/viewers/opposition-discord`) : `efface` ou `inscrit`, rien d'autre. */
async function oppositionDiscordRequest(discordId: string) {
  const response = await fetch("/api/viewers/opposition-discord", {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify({ discordId }),
  });
  const payload: unknown = await response.json().catch(() => null);
  const result = oppositionDiscordReponseSchema.safeParse(payload);
  if (!response.ok || !result.success) {
    throw new ApiError("opposition_failed", "Effacement en echec", response.status);
  }
  return { resultat: result.data.issue };
}

type Reseau = "twitch" | "discord";

export function OppositionForm() {
  const [reseau, setReseau] = useState<Reseau>("twitch");
  const [discordId, setDiscordId] = useState("");
  const [login, setLogin] = useState("");
  const [identifiant, setIdentifiant] = useState("");
  const [confirming, setConfirming] = useState(false);

  const opposition = useMutation({
    mutationFn: (corps: { twitch: OppositionRequete } | { discord: string }) =>
      "discord" in corps
        ? oppositionDiscordRequest(corps.discord)
        : oppositionRequest(corps.twitch),
    // Rien de la personne ne reste a l'ecran une fois la demande partie.
    onSettled: () => {
      setLogin("");
      setIdentifiant("");
      setDiscordId("");
    },
  });

  const body: OppositionRequete = {
    ...(identifiant.trim() === "" ? {} : { twitchUserId: identifiant.trim() }),
    ...(login.trim() === "" ? {} : { login: login.trim() }),
  };
  const discord = reseau === "discord";
  const empty = discord
    ? !IDENTIFIANT_DISCORD.test(discordId.trim())
    : login.trim() === "" && identifiant.trim() === "";
  // Ce que la confirmation rappelle : ce qui sera envoye, et l'identifiant d'abord.
  const target = discord
    ? discordId.trim()
    : identifiant.trim() === ""
      ? login.trim()
      : identifiant.trim();

  const message = opposition.isError
    ? { text: OPPOSITION_FAILED, tone: "problem" as const }
    : opposition.isSuccess
      ? OPPOSITION_MESSAGES[opposition.data.resultat]
      : null;

  return (
    <section aria-labelledby="opposition-titre">
      <Card className={CARTE}>
        <CardHeader>
          <CardTitle id="opposition-titre" className={TITRE_CARTE}>
            Effacer un viewer à sa demande
          </CardTitle>
          <p className="text-[20px] text-muted-foreground">
            Pour une personne qui s'oppose à ce que La Taverne note ses données : ses données sont
            effacées et plus rien n'est noté d'elle ensuite.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-[20px]">
          <fieldset className="flex gap-4" disabled={confirming || opposition.isPending}>
            <legend className="sr-only">Où la personne est connue</legend>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="opposition-reseau"
                checked={!discord}
                onChange={() => {
                  opposition.reset();
                  setReseau("twitch");
                }}
              />
              Twitch
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="opposition-reseau"
                checked={discord}
                onChange={() => {
                  opposition.reset();
                  setReseau("discord");
                }}
              />
              Discord
            </label>
          </fieldset>
          {discord ? (
            <>
              <label className="flex flex-col gap-1">
                Identifiant Discord
                <input
                  className="h-10 rounded-md border border-input bg-background px-3"
                  autoComplete="off"
                  inputMode="numeric"
                  disabled={confirming || opposition.isPending}
                  value={discordId}
                  onChange={(event) => setDiscordId(event.target.value)}
                />
              </label>
              <p className="text-muted-foreground">
                L'identifiant est un nombre de 17 à 20 chiffres (mode développeur de Discord, clic
                droit sur le membre, « Copier l'identifiant »). L'effacement est inscrit d'avance si
                la personne n'a jamais gagné de points.
              </p>
            </>
          ) : (
            <>
              <label className="flex flex-col gap-1">
                Login Twitch
                <input
                  className="h-10 rounded-md border border-input bg-background px-3"
                  autoComplete="off"
                  disabled={confirming || opposition.isPending}
                  value={login}
                  onChange={(event) => setLogin(event.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1">
                Identifiant Twitch
                <input
                  className="h-10 rounded-md border border-input bg-background px-3"
                  autoComplete="off"
                  inputMode="numeric"
                  disabled={confirming || opposition.isPending}
                  value={identifiant}
                  onChange={(event) => setIdentifiant(event.target.value)}
                />
              </label>
              <p className="text-muted-foreground">
                Un login ne suffit que si la personne a déjà été vue. Sinon, donne son identifiant
                Twitch : l'effacement est alors inscrit d'avance.
              </p>
            </>
          )}
          {confirming ? (
            <div className="flex flex-col items-start gap-2">
              <p>
                Effacer définitivement <strong>{target}</strong> et ne plus rien noter d'elle ?
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setConfirming(false);
                    opposition.mutate(discord ? { discord: discordId.trim() } : { twitch: body });
                  }}
                >
                  Confirmer l'effacement
                </Button>
                <Button variant="outline" onClick={() => setConfirming(false)}>
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Button
                disabled={empty || opposition.isPending}
                onClick={() => {
                  opposition.reset();
                  setConfirming(true);
                }}
              >
                Effacer ce viewer
              </Button>
            </div>
          )}
          {opposition.isPending ? (
            <p className="text-muted-foreground" role="status">
              Effacement en cours...
            </p>
          ) : message ? (
            <p className={cn(message.tone === "problem" && "text-destructive")} role="status">
              {message.text}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}

/** La levee d'une opposition (`POST /api/twitch/opposition/lever`) : le resultat est lu par le contrat, tout le reste est un echec. */
async function leverRequest(twitchUserId: string) {
  const response = await fetch("/api/twitch/opposition/lever", {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify({ twitchUserId }),
  });
  const payload: unknown = await response.json().catch(() => null);
  const result = oppositionLeveeReponseSchema.safeParse(payload);
  if (!response.ok || !result.success) {
    throw new ApiError("opposition_levee_failed", "Levee en echec", response.status);
  }
  return result.data;
}

/** Un identifiant Twitch : des chiffres, 20 au plus, comme le contrat (`shared/src/app/twitch.ts`). */
const IDENTIFIANT_TWITCH = /^\d{1,20}$/;

/**
 * « Lever une opposition » : le retour que la politique de confidentialite promet a qui a efface ses donnees (« pour revenir,
 * ecris a l'adresse de contact »). Elle appelle `POST /api/twitch/opposition/lever` par l'identifiant Twitch, derriere la
 * garde de la regie : la ligne d'`oppositions` part, rien d'autre, et ce qui a ete efface ne revient pas. Un seul champ, parce
 * qu'une opposition ne garde que l'identifiant. Comme l'effacement, le bouton demande une confirmation ; la page ecrit le
 * resultat (`regie-texts.ts`), jamais un texte du serveur, et ne garde rien de la personne une fois la demande partie.
 */
export function LeverOppositionForm() {
  const [identifiant, setIdentifiant] = useState("");
  const [confirming, setConfirming] = useState(false);

  const levee = useMutation({
    mutationFn: leverRequest,
    // Rien de la personne ne reste a l'ecran une fois la demande partie.
    onSettled: () => setIdentifiant(""),
  });

  const cible = identifiant.trim();
  const message = levee.isError
    ? { text: OPPOSITION_LEVEE_FAILED, tone: "problem" as const }
    : levee.isSuccess
      ? OPPOSITION_LEVEE_MESSAGES[levee.data.resultat]
      : null;

  return (
    <section aria-labelledby="lever-opposition-titre">
      <Card className={CARTE}>
        <CardHeader>
          <CardTitle id="lever-opposition-titre" className={TITRE_CARTE}>
            Lever une opposition
          </CardTitle>
          <p className="text-[20px] text-muted-foreground">
            Pour une personne qui t'a écrit pour revenir : son opposition est retirée, La Taverne
            peut de nouveau la noter et elle peut rejoindre. Ce qui a été effacé ne revient pas.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-[20px]">
          <label className="flex flex-col gap-1">
            Identifiant Twitch de la personne
            <input
              className="h-10 rounded-md border border-input bg-background px-3"
              autoComplete="off"
              inputMode="numeric"
              disabled={confirming || levee.isPending}
              value={identifiant}
              onChange={(event) => setIdentifiant(event.target.value)}
            />
          </label>
          <p className="text-muted-foreground">
            Donne son identifiant Twitch (des chiffres), pas son login : une opposition ne garde que
            l'identifiant. Demande-le à la personne : rien en base ne relie plus son login à son
            identifiant.
          </p>
          {confirming ? (
            <div className="flex flex-col items-start gap-2">
              <p>
                Lever l'opposition de <strong>{cible}</strong> ? La Taverne pourra de nouveau la
                noter, et elle pourra rejoindre.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setConfirming(false);
                    levee.mutate(cible);
                  }}
                >
                  Confirmer la levée
                </Button>
                <Button variant="outline" onClick={() => setConfirming(false)}>
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Button
                disabled={!IDENTIFIANT_TWITCH.test(cible) || levee.isPending}
                onClick={() => {
                  levee.reset();
                  setConfirming(true);
                }}
              >
                Lever l'opposition
              </Button>
            </div>
          )}
          {levee.isPending ? (
            <p className="text-muted-foreground" role="status">
              Levée en cours...
            </p>
          ) : message ? (
            <p className={cn(message.tone === "problem" && "text-destructive")} role="status">
              {message.text}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}

/** La levee d'une opposition Discord (`POST /api/viewers/opposition-discord/lever`) : le resultat est lu par le contrat, tout le reste est un echec. */
async function leverDiscordRequest(discordId: string) {
  const response = await fetch("/api/viewers/opposition-discord/lever", {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify({ discordId }),
  });
  const payload: unknown = await response.json().catch(() => null);
  const result = oppositionDiscordLeveeReponseSchema.safeParse(payload);
  if (!response.ok || !result.success) {
    throw new ApiError("opposition_levee_failed", "Levee en echec", response.status);
  }
  return result.data;
}

/**
 * « Lever une opposition Discord » : le meme retour que `LeverOppositionForm`, pour un membre de Discord. Sans lui, une
 * personne qui a efface ses donnees (« Effacer mes donnees » emporte aussi son membre Discord) resterait refusee sur Discord
 * pour toujours : un gain de L'Ecuyer est refuse et elle n'obtient plus de code pour `/lier`. Elle appelle
 * `POST /api/viewers/opposition-discord/lever` par l'identifiant Discord, derriere la garde de la regie : la ligne
 * d'`oppositions_discord` part, rien d'autre, et ce qui a ete efface ne revient pas. Un seul champ, parce qu'une opposition ne
 * garde que l'identifiant. Comme les autres, le bouton demande une confirmation ; la page ecrit le resultat (`regie-texts.ts`),
 * jamais un texte du serveur, et ne garde rien de la personne une fois la demande partie.
 */
export function LeverOppositionDiscordForm() {
  const [identifiant, setIdentifiant] = useState("");
  const [confirming, setConfirming] = useState(false);

  const levee = useMutation({
    mutationFn: leverDiscordRequest,
    // Rien de la personne ne reste a l'ecran une fois la demande partie.
    onSettled: () => setIdentifiant(""),
  });

  const cible = identifiant.trim();
  const message = levee.isError
    ? { text: OPPOSITION_LEVEE_FAILED, tone: "problem" as const }
    : levee.isSuccess
      ? OPPOSITION_DISCORD_LEVEE_MESSAGES[levee.data.issue]
      : null;

  return (
    <section aria-labelledby="lever-opposition-discord-titre">
      <Card className={CARTE}>
        <CardHeader>
          <CardTitle id="lever-opposition-discord-titre" className={TITRE_CARTE}>
            Lever une opposition Discord
          </CardTitle>
          <p className="text-[20px] text-muted-foreground">
            Pour un membre du Discord qui t'a écrit pour revenir : son opposition est retirée,
            L'Écuyer peut de nouveau noter ses gains et il peut relier son compte Twitch. Ce qui a
            été effacé ne revient pas.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-[20px]">
          <label className="flex flex-col gap-1">
            Identifiant Discord du membre
            <input
              className="h-10 rounded-md border border-input bg-background px-3"
              autoComplete="off"
              inputMode="numeric"
              disabled={confirming || levee.isPending}
              value={identifiant}
              onChange={(event) => setIdentifiant(event.target.value)}
            />
          </label>
          <p className="text-muted-foreground">
            Donne son identifiant Discord (17 à 20 chiffres : mode développeur de Discord, clic
            droit sur le membre, « Copier l'identifiant »), pas son pseudo. Demande-le à la personne
            : rien en base ne relie plus son pseudo à son identifiant.
          </p>
          {confirming ? (
            <div className="flex flex-col items-start gap-2">
              <p>
                Lever l'opposition de <strong>{cible}</strong> ? L'Écuyer pourra de nouveau noter ce
                membre, et il pourra relier son compte.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setConfirming(false);
                    levee.mutate(cible);
                  }}
                >
                  Confirmer la levée Discord
                </Button>
                <Button variant="outline" onClick={() => setConfirming(false)}>
                  Annuler
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Button
                disabled={!IDENTIFIANT_DISCORD.test(cible) || levee.isPending}
                onClick={() => {
                  levee.reset();
                  setConfirming(true);
                }}
              >
                Lever l'opposition Discord
              </Button>
            </div>
          )}
          {levee.isPending ? (
            <p className="text-muted-foreground" role="status">
              Levée en cours...
            </p>
          ) : message ? (
            <p className={cn(message.tone === "problem" && "text-destructive")} role="status">
              {message.text}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </section>
  );
}
